import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Palette, Upload, ImageIcon } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resolveImg } from "@/lib/imageUtils";

export default function OrgBrandingPage() {
  const [b, setB] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org/branding");
        // Backend returns the branding row directly under `data`. Guard
        // against `null`/`undefined` so the inputs can safely read fields
        // without crashing.
        setB(data.data || {});
      } finally { setLoading(false); }
    })();
  }, []);

  const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
  const normalizeHex = (v, fallback) => {
    if (!v) return fallback;
    const s = String(v).trim();
    // Accept #RGB shorthand and expand to #RRGGBB
    if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
      return "#" + s.slice(1).split("").map((c) => c + c).join("");
    }
    if (HEX_RE.test(s)) return s;
    return fallback;
  };

  const submit = async () => {
    setSaving(true);
    try {
      // Backend validates primary/secondary as strict #RRGGBB. Coerce any
      // legacy or user-entered noise back to a valid value before PUT so the
      // save never fails on a malformed hex.
      const payload = {
        ...b,
        primary_color: normalizeHex(b.primary_color, "#101876"),
        secondary_color: normalizeHex(b.secondary_color, "#329112"),
      };
      setB(payload);
      await api.put("/org/branding", payload);
      toast.success("Branding saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB."); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/org/branding/upload-logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns the stored path — save it into state so the preview
      // re-renders and the next PUT keeps the field.
      setB((prev) => ({ ...prev, organization_logo: data.data.logo_path }));
      toast.success("Logo uploaded. Click Save branding to publish.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (loading) return <DashboardLayout title="Branding"><LoadingBlock label="Loading…" /></DashboardLayout>;

  const logoUrl = b.organization_logo ? resolveImg(b.organization_logo) : "";

  return (
    <DashboardLayout title="Branding" breadcrumbs={["My Institute", "Branding"]}>
      <div className="max-w-3xl rounded-2xl border border-slate-200 bg-white p-6" data-testid="org-branding-page">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]"><Palette className="h-5 w-5" /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Institute branding</div>
            <div className="text-sm font-bold text-slate-900">Logo & colors for your dashboards and (future) certificates</div>
          </div>
        </div>

        {/* Logo upload */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Institute logo</Label>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <div className="flex h-24 w-40 items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-white">
              {logoUrl ? (
                <img src={logoUrl} alt="Institute logo" className="max-h-full max-w-full object-contain" data-testid="org-branding-logo-preview" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-slate-400">
                  <ImageIcon className="h-6 w-6" />
                  <span className="text-[10px] font-semibold uppercase tracking-widest">No logo</span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="org-branding-logo-file"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-60"
                data-testid="org-branding-logo-upload"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {b.organization_logo ? "Replace logo" : "Upload logo"}
              </button>
              {b.organization_logo && (
                <button
                  type="button"
                  onClick={() => setB((prev) => ({ ...prev, organization_logo: "" }))}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                  data-testid="org-branding-logo-remove"
                >
                  Remove logo
                </button>
              )}
              <p className="text-[11px] text-slate-500 max-w-xs">PNG, JPG, WEBP or SVG. Max 5 MB. Transparent PNG works best.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Login banner URL (optional)</Label>
            <Input value={b.login_background_image || ""} onChange={(e) => setB({ ...b, login_background_image: e.target.value })} data-testid="org-branding-login-banner" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Footer text</Label>
            <Input value={b.footer_text || ""} onChange={(e) => setB({ ...b, footer_text: e.target.value })} data-testid="org-branding-footer" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Primary color</Label>
            <div className="flex items-center gap-3">
              <input type="color" value={normalizeHex(b.primary_color, "#101876")} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
              <Input
                value={b.primary_color || ""}
                onChange={(e) => setB({ ...b, primary_color: e.target.value })}
                data-testid="org-branding-primary"
                placeholder="#101876"
                className={b.primary_color && !HEX_RE.test(b.primary_color) ? "border-rose-400 focus-visible:ring-rose-300" : ""}
              />
            </div>
            {b.primary_color && !HEX_RE.test(b.primary_color) && (
              <p className="text-[11px] font-semibold text-rose-600">Use format <code>#RRGGBB</code> — will be reset to #101876 on save.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Secondary color</Label>
            <div className="flex items-center gap-3">
              <input type="color" value={normalizeHex(b.secondary_color, "#329112")} onChange={(e) => setB({ ...b, secondary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
              <Input
                value={b.secondary_color || ""}
                onChange={(e) => setB({ ...b, secondary_color: e.target.value })}
                data-testid="org-branding-secondary"
                placeholder="#329112"
                className={b.secondary_color && !HEX_RE.test(b.secondary_color) ? "border-rose-400 focus-visible:ring-rose-300" : ""}
              />
            </div>
            {b.secondary_color && !HEX_RE.test(b.secondary_color) && (
              <p className="text-[11px] font-semibold text-rose-600">Use format <code>#RRGGBB</code> — will be reset to #329112 on save.</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="org-branding-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save branding
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
